/**
 * A bunch of form-related helper functions, including
 *
 * - Auto-select 'Other' radio button, when text is entered
 */

import { getParentElement } from './dom-helpers';

class FormHelpers {
  /**
   * @param {object} [options] -
   * @param {string} [options.otherTextInput] - Selector for 'other' text input element, within a radio box component
   * @param {string} [options.timeInputs] - Selector for 'hours' and 'minutes' input elements, within a time input component
   * @param {string} [options.filterForms] - Selector for form elements that should submit when a value changes
   */
  constructor(options) {
    const defaults = {
      otherTextInput: '.radio-box__textinput',
      timeInputs: 'input.time-input__hours,input.time-input__minutes',
      filterForms: 'form.list-filters__form',
      excludedInputs: ['freeSearch'],
      clearButtonSelector: '[data-clear-target]',
    }
    Object.assign(this, defaults, options);
  }

  init() {
    this.radioTexts = Array.from(
      document.querySelectorAll(this.otherTextInput)
    );
    this.radioTexts.forEach(input => {
      input.addEventListener('change', this.checkRelatedRadio.bind(this))
    });

    this.timeInputs = document.querySelectorAll(this.timeInputs);
    this.timeInputs.forEach(input => {
      input.addEventListener('input', this.handleTimeInput.bind(this));
    });

    this.filterForms = document.querySelectorAll(this.filterForms);
    this.filterForms.forEach(form => {
      form.addEventListener('change', this.handleFilterChange.bind(this));
      form.addEventListener('submit', this.handleFormSubmit.bind(this));
    });

    this.clearButtons = document.querySelectorAll(this.clearButtonSelector);
    this.clearButtons.forEach(button => {
      button.addEventListener('click', this.handleClearButtonClick.bind(this));
    });
  }

  /**
   * Event listener for checking the radio button corresponding to 'other' text input element
   *
   * @param {object} event - The event object
   */
  checkRelatedRadio(event) {
    const input = event.currentTarget;
    const label = input.parentNode;
    const radio = document.getElementById(label.htmlFor);
    if(input.value !== '') {
      radio.checked = true;
    } else {
      radio.checked = false;
    }
  }


  addLeadingZero(subject) {
    subject.value = ('00' + subject.value).substr(-2, 2);
  }

  handleTimeInput(event) {
    this.addLeadingZero(event.currentTarget);
  }

  /**
   * Disables valueless inputs before submitting the associated form
   *
   * @param {Event} event - A 'change' event from an input/select
   */
  handleFilterChange(event) {
    if(this.excludedInputs.indexOf(event.target.id) >= 0) {
      return;
    }
    this.disableValuelessInputs(event.currentTarget);
    event.currentTarget.submit();
  }

  /**
   * Disables valueless inputs without exclusively submitting the form
   *
   * @param {Event} event - The initiating event, e.g. 'submit'
   */
  handleFormSubmit(event) {
    this.disableValuelessInputs(event.currentTarget);
  }

  /**
   * Disables valueless inputs, based on a given context
   *
   * @param {HTMLElement} context - A form element, or an input element within the form
   */
  disableValuelessInputs(context) {
    const noSelection = this.getValuelessInputs(context);
    noSelection.forEach(this.inputDisable(true));
  }

  /**
   * Finds valueless inputs and selects within the form context of the given element
   *
   * @param {HTMLElement} inputEl - The element to start searching from
   * @returns {Array} - List of elements that's value equals ''
   */
  getValuelessInputs(inputEl) {
    const form = getParentElement(inputEl, 'form');
    return Array.from(
      form.querySelectorAll('input,select')
    ).filter(el => el.value === '');
  }

  /**
   * Sets an element's 'disabled' state
   *
   * @param {Boolean} state - Truthy values disable, falsey values enable
   * @return {Function} - A function callable within a forEach
   */
  inputDisable(state) {
    return function(element) {
      element.toggleAttribute('disabled', state);
    };
  }

  /**
   * Clears an input referenced in the event target's data-clear-target attribute.
   * Attempts to use flatpickr's clear method if available.
   *
   * @param {Event} event - The initiating event
   */
  handleClearButtonClick(event) {
    const targetSelector = event.currentTarget.dataset.clearTarget;
    const target = document.querySelector(targetSelector);

    if(target._flatpickr) {
      target._flatpickr.clear();
    }
    else if(target) {
      target.value = '';
    }
  }
}

module.exports = FormHelpers;
